import React, {Component} from 'react';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;

// Loaded once per page, not once per mount, so navigating back to Sign Up doesn't
// stack up duplicate <script> tags.
function loadTurnstileScript() {
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Cloudflare Turnstile widget.
 *
 * Calls onVerify(token) when the visitor passes, and onVerify('') whenever the token
 * stops being good (expired, errored). The parent treats an empty token as "not verified
 * yet", so an expired challenge disables the submit button again rather than sending a
 * request the API is only going to reject.
 */
export class Turnstile extends Component {
  _container = React.createRef();
  _widgetId = null;
  _mounted = false;

  componentDidMount() {
    this._mounted = true;

    const {siteKey} = this.props;
    if (!siteKey) {
      return;
    }

    loadTurnstileScript()
      .then(() => {
        if (!this._mounted || !window.turnstile || !this._container.current) {
          return;
        }

        this._widgetId = window.turnstile.render(this._container.current, {
          sitekey: siteKey,
          callback: token => this._emit(token),
          'expired-callback': () => this._emit(''),
          'timeout-callback': () => this._emit(''),
          'error-callback': () => this._emit(''),
        });
      })
      .catch(() => {
        // Cloudflare unreachable. Leave the token empty so the form stays blocked
        // rather than letting through a request the API will reject anyway.
        this._emit('');
      });
  }

  componentWillUnmount() {
    this._mounted = false;

    if (this._widgetId && window.turnstile) {
      window.turnstile.remove(this._widgetId);
      this._widgetId = null;
    }
  }

  _emit = token => {
    const {onVerify} = this.props;
    if (this._mounted && onVerify) {
      onVerify(token);
    }
  };

  // Called by the parent after a failed submit so the visitor gets a fresh challenge.
  reset() {
    if (this._widgetId && window.turnstile) {
      window.turnstile.reset(this._widgetId);
      this._emit('');
    }
  }

  render() {
    return <div ref={this._container} />;
  }
}

export default Turnstile;
